import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { enqueueSnackbar } from 'notistack';
import {
  ExportHubAPI,
  ExportProduct,
  ExportLicense,
  LicenseDrawdown,
} from 'api/ExportHubAPI';
import { LicenseStatusPill, formatCurrency } from './licensePresets';
import { licenseCoveredCountries } from './coverage';
import LinkLicenseModal from './LinkLicenseModal';
import LicenseRequestWizard from './LicenseRequestWizard';
import RecordDrawdownModal from './RecordDrawdownModal';

const fmtDate = (d: string | null): string =>
  d ? moment(d).format('MM/DD/YYYY') : '—';

interface Props {
  product: ExportProduct;
  groupId: string | undefined;
  onChanged: () => void;
  // Effective destinations (spec §4.7): intended ∪ linked entity countries.
  // Falls back to the product's intended destinations when not supplied.
  effectiveDestinations?: string[];
}

const LinkedLicenses: React.FC<Props> = ({
  product,
  groupId,
  onChanged,
  effectiveDestinations,
}) => {
  const [licenses, setLicenses] = useState<ExportLicense[]>([]);
  const [drawdowns, setDrawdowns] = useState<LicenseDrawdown[]>([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordLicenseId, setRecordLicenseId] = useState<string | undefined>();

  const load = useCallback(async () => {
    if (!groupId) return;
    try {
      const [lics, dds] = await Promise.all([
        ExportHubAPI.getLinkedLicenses(product.id, groupId),
        ExportHubAPI.getProductDrawdowns(product.id, groupId),
      ]);
      setLicenses(lics);
      setDrawdowns(dds);
    } catch (err) {
      console.error('Failed to load linked licenses:', err);
    }
  }, [product.id, groupId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * What this product has drawn on each license. Posted rows only — drafts are
   * staged and voids are reversed, so neither belongs in a consumed figure.
   */
  const drawnByLicense = useMemo(() => {
    const totals: Record<string, number> = {};
    drawdowns.forEach((d) => {
      if (d.status !== 'Posted') return;
      totals[d.license_id] = (totals[d.license_id] || 0) + d.value;
    });
    return totals;
  }, [drawdowns]);

  const unlink = async (l: ExportLicense) => {
    if (!groupId) return;
    try {
      await ExportHubAPI.unlinkLicense(product.id, l.id, groupId);
      enqueueSnackbar('License unlinked.', { variant: 'success' });
      load();
      onChanged();
    } catch (err) {
      enqueueSnackbar('Failed to unlink license.', { variant: 'error' });
    }
  };

  const destinations =
    effectiveDestinations ?? product.intended_destination_countries ?? [];
  const coveredCountries = new Set(
    licenses.flatMap(licenseCoveredCountries).map((c) => c.toLowerCase()),
  );
  const missing = destinations.filter(
    (d) => d && !coveredCountries.has(d.toLowerCase()),
  );
  const fullyCovered = destinations.length > 0 && missing.length === 0;

  return (
    <div className='eh-panel'>
      <div className='eh-panel-title eh-panel-title-flex'>
        <span>
          Linked Licenses{' '}
          <span className='eh-count-soft'>({licenses.length})</span>
        </span>
        <div className='eh-panel-head-actions'>
          <button
            className='eh-mini-btn'
            type='button'
            onClick={() => setLinkOpen(true)}
          >
            <LinkOutlinedIcon style={{ fontSize: 15 }} /> Link License
          </button>
          <button
            className='eh-mini-btn'
            type='button'
            onClick={() => setWizardOpen(true)}
          >
            <AddIcon style={{ fontSize: 15 }} /> Add License
          </button>
        </div>
      </div>

      {/* License coverage verification (spec §4.9). */}
      {licenses.length > 0 && destinations.length > 0 && (
        missing.length > 0 ? (
          <div className='eh-cov-banner eh-sev-box-warning'>
            <WarningAmberIcon
              className='eh-sev-warning'
              style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}
            />
            <span>
              License coverage gap — no linked license covers{' '}
              <strong>{missing.join(', ')}</strong>. Link a license or add a
              new one.
            </span>
          </div>
        ) : fullyCovered ? (
          <div className='eh-cov-banner eh-sev-box-success'>
            <CheckCircleIcon
              className='eh-sev-success'
              style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}
            />
            <span>
              All effective destinations are covered by at least one linked
              license.
            </span>
          </div>
        ) : null
      )}

      {licenses.length === 0 ? (
        <div className='eh-lic-empty'>
          <div className='eh-detail-note'>
            No licenses linked to this product yet.
          </div>
          <div className='eh-panel-head-actions'>
            <button
              className='btn-outline'
              type='button'
              onClick={() => setLinkOpen(true)}
            >
              <LinkOutlinedIcon
                style={{ fontSize: 15, marginRight: 6, verticalAlign: 'middle' }}
              />
              Link License
            </button>
            <button
              className='btn-primary'
              type='button'
              onClick={() => setWizardOpen(true)}
            >
              <AddIcon
                style={{ fontSize: 15, marginRight: 6, verticalAlign: 'middle' }}
              />
              Add License
            </button>
          </div>
        </div>
      ) : (
        licenses.map((l) => {
          const remaining = l.value !== null ? l.value - l.value_used : null;
          // This product's share, not the whole license's: drawn from the
          // ledger, allocated from the join row's planning ceiling.
          const drawn = drawnByLicense[l.id] || 0;
          const allocated = l.allocated_value ?? 0;
          const pct =
            allocated > 0
              ? Math.min(100, Math.round((drawn / allocated) * 100))
              : 0;
          return (
            <div className='eh-linked-row eh-dd-linked-row' key={l.id}>
              <div className='eh-linked-info'>
                <span className='eh-linked-titleline'>
                  <span className='eh-linked-title eh-mono'>
                    {l.license_number || l.license_type}
                  </span>
                  <span className='eh-pill eh-pill-muted'>{l.license_type}</span>
                  <span className='eh-lic-agency'>{l.agency}</span>
                  <LicenseStatusPill value={l.status} />
                </span>
                <span className='eh-linked-meta'>
                  {fmtDate(l.effective_date)} → {fmtDate(l.expiry_date)} ·{' '}
                  {remaining !== null
                    ? `${formatCurrency(remaining)} remaining on the license`
                    : `${formatCurrency(l.proposed_value)} proposed`}
                </span>
              </div>
              <div className='eh-dd-linked-actions'>
                <button
                  className='eh-mini-btn'
                  type='button'
                  onClick={() => {
                    setRecordLicenseId(l.id);
                    setRecordOpen(true);
                  }}
                >
                  Record
                </button>
                <button
                  className='eh-linked-x'
                  type='button'
                  onClick={() => unlink(l)}
                  title='Unlink license'
                  aria-label='Unlink license'
                >
                  <CloseIcon style={{ fontSize: 14 }} />
                </button>
              </div>
              {(drawn !== 0 || allocated > 0) && (
                <div className='eh-dd-alloc'>
                  <span className='eh-dd-alloc-figure'>
                    {formatCurrency(drawn)} drawn by this product
                    {allocated > 0
                      ? ` of ${formatCurrency(allocated)} allocated`
                      : ''}
                  </span>
                  {allocated > 0 && (
                    <span className='eh-val-bar eh-val-bar-sm eh-dd-alloc-bar'>
                      <span
                        className={`eh-val-bar-fill${
                          drawn > allocated
                            ? ' eh-dd-fill-over'
                            : pct > 75
                            ? ' eh-dd-fill-warn'
                            : ''
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}

      <LinkLicenseModal
        open={linkOpen}
        databaseId={product.database_id || ''}
        productId={product.id}
        groupId={groupId}
        productDestinations={destinations}
        linkedIds={licenses.map((l) => l.id)}
        productTier={product.tier}
        onClose={() => setLinkOpen(false)}
        onLinked={() => {
          setLinkOpen(false);
          load();
          onChanged();
        }}
        onRequestNew={() => {
          setLinkOpen(false);
          setWizardOpen(true);
        }}
      />
      <RecordDrawdownModal
        open={recordOpen}
        groupId={groupId}
        licenses={licenses}
        licenseId={recordLicenseId}
        lockedProductId={product.id}
        onClose={() => setRecordOpen(false)}
        onSaved={() => {
          setRecordOpen(false);
          load();
          onChanged();
        }}
      />
      <LicenseRequestWizard
        open={wizardOpen}
        databaseId={product.database_id || ''}
        groupId={groupId}
        lockedProductId={product.id}
        lockedProductName={product.name}
        onClose={() => setWizardOpen(false)}
        onCreated={() => {
          setWizardOpen(false);
          load();
          onChanged();
        }}
      />
    </div>
  );
};

export default LinkedLicenses;
